import React from 'react';
import { RED, INK, GREY, LIGHT, TEAL, BORDER, wrap, Hero, Crumbs, CtaBand } from '../seo/SeoUI';
import { GUIDE_PAGES, GUIDE_LIST, GUIDES_CTA } from './data';
import EmailCaptureCard from './EmailCaptureCard';

// /guides — the Home Resource Center. Magazine-style hub: featured seasonal article,
// then the three pillars (Home care & maintenance · Cost guides · Hiring & how-to).

const ICONS = {
  'home-maintenance-checklist': '🏠',
  'fall-home-maintenance-checklist': '🍂',
  '10-small-problems-big-repairs': '⚠️',
  'gutter-cleaning-maintenance': '🌧️',
  'hvac-maintenance-guide': '🌡️',
  'water-heater-maintenance': '🚿',
  'dryer-vent-cleaning-safety': '🔥',
  'caulking-grout-guide': '🛁',
  'new-homeowner-first-year': '🔑',
  'handyman-cost': '🔧',
  'tv-mounting-cost': '📺',
  'furniture-assembly-cost': '🪑',
  'house-cleaning-cost': '🧹',
  'plumber-cost': '🚰',
  'electrician-cost': '💡',
  'lawn-care-cost': '🌿',
  'junk-removal-cost': '🚛',
  'interior-painting-cost': '🎨',
  'moving-help-cost': '📦',
  'how-to-hire-a-handyman': '🤝',
};

const PILLARS = [
  {
    key: 'Home care',
    eyebrow: 'Home care & maintenance',
    title: 'Take care of your home before it needs rescuing',
    blurb: 'Seasonal checklists and prevention guides — the small tasks that stop the expensive repairs.',
    accent: TEAL,
  },
  {
    key: 'Cost guide',
    eyebrow: 'Cost guides',
    title: 'Know what a fair price looks like',
    blurb: 'Honest 2026 price ranges for the jobs people book most, before you talk to anyone.',
    accent: RED,
  },
  {
    key: 'How-to',
    eyebrow: 'Hiring & how-to',
    title: 'Hire with confidence',
    blurb: 'What to ask, what to check, and how to get the most from every pro visit.',
    accent: INK,
  },
];

function Card({ g, accent }) {
  return (
    <a
      href={`/guides/${g.slug}`}
      style={{ display: 'flex', flexDirection: 'column', background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 16, overflow: 'hidden', textDecoration: 'none', transition: 'box-shadow .15s' }}
    >
      <div aria-hidden="true" style={{ height: 86, background: `linear-gradient(135deg, ${accent}12, ${accent}26)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 38, borderBottom: `1px solid ${BORDER}` }}>
        {ICONS[g.slug] || '📄'}
      </div>
      <div style={{ padding: '18px 20px 20px', display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
        <h3 style={{ fontSize: 17.5, fontWeight: 700, margin: 0, color: INK, lineHeight: 1.35 }}>{g.h1}</h3>
        <p style={{ fontSize: 13.5, color: GREY, margin: 0, lineHeight: 1.55, flex: 1 }}>{g.metaDescription}</p>
        <span style={{ fontSize: 13, color: accent, fontWeight: 700 }}>
          Read guide → {g.readTime ? <span style={{ color: GREY, fontWeight: 500 }}>· {g.readTime} min</span> : null}
        </span>
      </div>
    </a>
  );
}

function GuidesIndex() {
  const breadcrumbItems = [{ label: 'Home', href: '/' }, { label: 'Guides' }];

  const featured = GUIDE_LIST.find((g) => g.featured);
  const featuredData = featured ? GUIDE_PAGES[featured.slug] : null;

  const byPillar = PILLARS.map((p) => ({
    ...p,
    items: GUIDE_LIST.filter((g) => {
      if (featured && g.slug === featured.slug) return false;
      if (p.key === 'How-to') return g.category !== 'Home care' && g.category !== 'Cost guide';
      return g.category === p.key;
    }),
  })).filter((p) => p.items.length > 0);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'BestHand Home Resource Center',
      description: 'Seasonal home maintenance checklists, honest cost guides and hiring advice from the BestHand team.',
      publisher: { '@type': 'Organization', name: 'BestHand' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbItems.map((b, i) => ({
        '@type': 'ListItem', position: i + 1, name: b.label, ...(b.href ? { item: b.href } : {}),
      })),
    },
  ];

  return (
    <div style={{ background: '#fff' }}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <Hero
        breadcrumb={<Crumbs items={breadcrumbItems} />}
        eyebrow="The Home Resource Center"
        title="Everything your home wishes you knew"
        subtitle="Seasonal maintenance checklists, honest cost guides and hiring advice — free, practical, and written to save you from expensive surprises."
      >
        <a href={GUIDES_CTA} style={{ display: 'inline-block', padding: '13px 22px', borderRadius: 12, background: RED, color: '#fff', fontWeight: 700, fontSize: 16, textDecoration: 'none' }}>Book a trusted pro</a>
      </Hero>

      {featuredData ? (
        <section style={{ ...wrap, padding: '44px 20px 8px' }}>
          <a href={`/guides/${featured.slug}`} style={{ display: 'flex', flexWrap: 'wrap', border: `1px solid ${BORDER}`, borderRadius: 20, overflow: 'hidden', textDecoration: 'none', background: '#fff' }}>
            <div aria-hidden="true" style={{ flex: '1 1 320px', minWidth: 280, minHeight: 220, background: `linear-gradient(135deg, ${TEAL}18, ${RED}14)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 84 }}>
              {ICONS[featured.slug] || '🏠'}
            </div>
            <div style={{ flex: '2 1 420px', minWidth: 300, padding: '30px 34px', display: 'flex', flexDirection: 'column', gap: 10, justifyContent: 'center' }}>
              <span style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: '.6px', textTransform: 'uppercase', color: TEAL }}>⭐ Featured · {featuredData.category}</span>
              <h2 style={{ fontSize: 26, fontWeight: 800, margin: 0, color: INK, lineHeight: 1.25 }}>{featuredData.h1}</h2>
              <p style={{ fontSize: 15, color: GREY, margin: 0, lineHeight: 1.65, maxWidth: 560 }}>{featuredData.metaDescription}</p>
              <span style={{ fontSize: 14.5, color: RED, fontWeight: 700 }}>Read the full checklist → <span style={{ color: GREY, fontWeight: 500 }}>· {featuredData.readTime} min read</span></span>
            </div>
          </a>
        </section>
      ) : null}

      {byPillar.map((p, idx) => (
        <section key={p.key} style={{ background: idx % 2 === 1 ? LIGHT : '#fff', borderTop: idx > 0 ? `1px solid ${BORDER}` : 'none' }}>
          <div style={{ ...wrap, padding: '46px 20px 50px' }}>
            <span style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: '.7px', textTransform: 'uppercase', color: p.accent }}>{p.eyebrow}</span>
            <h2 style={{ fontSize: 27, fontWeight: 800, margin: '6px 0 6px', color: INK }}>{p.title}</h2>
            <p style={{ fontSize: 15, color: GREY, margin: '0 0 24px', maxWidth: 640, lineHeight: 1.6 }}>{p.blurb}</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 18 }}>
              {p.items.map((g) => <Card key={g.slug} g={g} accent={p.accent === INK ? RED : p.accent} />)}
            </div>
          </div>
        </section>
      ))}

      <section style={{ ...wrap, padding: '10px 20px 42px' }}>
        <EmailCaptureCard compact />
      </section>

      {/* COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod. */}
      <CtaBand title="Ready to book?" subtitle="Compare upfront prices from background-checked local pros and get it done." ctaText="Get started" ctaHref={GUIDES_CTA} />
    </div>
  );
}

export default GuidesIndex;
